import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { transportApi, type Vehicle, type Route, type StopInput } from '../lib/transportApi';
import { ApiError, asList } from '../lib/api';
import { ModuleStrip } from '../components/ModuleStrip';

/// Transport administration: vehicles, routes & stops, and the "bus departed" safety alert, plus a
/// Staff-gated. (Drop-change requests now live in the Requests inbox, not here.)
export function TransportPage() {
  const [tab, setTab] = useState<'routes' | 'vehicles'>('routes');
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Transport</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Transport</h1>
      </div>
      <ModuleStrip module="transport" />

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'routes'} onClick={() => setTab('routes')}>Routes &amp; stops</TabBtn>
        <TabBtn active={tab === 'vehicles'} onClick={() => setTab('vehicles')}>Vehicles</TabBtn>
      </div>
      {tab === 'routes' && <RoutesTab />}
      {tab === 'vehicles' && <VehiclesTab />}
    </div>
  );
}

function VehiclesTab() {
  const [rows, setRows] = useState<Vehicle[]>([]);
  const [f, setF] = useState({ name: '', registrationNumber: '', capacity: 40, driverName: '', driverMobile: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { transportApi.listVehicles().then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  const add = async () => {
    if (!f.name.trim()) return;
    setBusy(true);
    try { await transportApi.createVehicle({ ...f, name: f.name.trim() }); setF({ name: '', registrationNumber: '', capacity: 40, driverName: '', driverMobile: '' }); load(); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 620 }}>
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Add a vehicle</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input className="input" placeholder="Bus 3" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input className="input mono" placeholder="KA-05-AB-1234" value={f.registrationNumber} onChange={(e) => setF({ ...f, registrationNumber: e.target.value })} />
          <input className="input" placeholder="Driver name" value={f.driverName} onChange={(e) => setF({ ...f, driverName: e.target.value })} />
          <input className="input" placeholder="Driver mobile" value={f.driverMobile} onChange={(e) => setF({ ...f, driverMobile: e.target.value })} />
          <input className="input" type="number" placeholder="Capacity" value={f.capacity} onChange={(e) => setF({ ...f, capacity: Number(e.target.value) })} />
          <button className="btn btn-accent" disabled={!f.name.trim() || busy} onClick={add}>Add vehicle</button>
        </div>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {rows.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>No vehicles yet.</div> : rows.map((v) => (
          <div key={v.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--line-2)' }}>
            <strong>{v.name}</strong>
            {v.registrationNumber && <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{v.registrationNumber}</span>}
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>cap {v.capacity}</span>
            {v.driverName && <span style={{ fontSize: 12, color: 'var(--ink-2)', marginLeft: 'auto' }}>{v.driverName}{v.driverMobile ? ` · ${v.driverMobile}` : ''}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function RoutesTab() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [newName, setNewName] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    transportApi.listRoutes().then((r) => setRoutes(asList(r))).catch(() => setRoutes([]));
    transportApi.listVehicles().then((r) => setVehicles(asList(r))).catch(() => setVehicles([]));
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    const r = await transportApi.createRoute({ name: newName.trim() });
    setNewName(''); setSelected(r.id); load();
  };

  const busDeparted = async (routeId: string) => {
    setMsg(null);
    try { const { notified } = await transportApi.tripAlert(routeId, 'Departed'); setMsg(`Bus-departed alert sent to ${notified} recipient(s).`); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not send.'); }
  };

  const route = routes.find((r) => r.id === selected) ?? null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
      <div>
        <div className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" placeholder="Route A — North" value={newName} onChange={(e) => setNewName(e.target.value)} style={{ flex: 1 }} />
            <button className="btn btn-accent" disabled={!newName.trim()} onClick={create}>+</button>
          </div>
        </div>
        <div className="card" style={{ overflow: 'hidden' }}>
          {routes.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No routes yet.</div> :
            routes.map((r) => (
              <button key={r.id} onClick={() => setSelected(r.id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: selected === r.id ? 'var(--paper,#F5F4F0)' : 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer' }}>
                <strong style={{ fontSize: 14 }}>{r.name}</strong>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.stops.length} stop(s)</div>
              </button>
            ))}
        </div>
      </div>

      <div>
        {msg && <div className="pill pill-muted" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{msg}</div>}
        {!route ? <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Select or create a route to edit its stops.</div> :
          <RouteEditor key={route.id} route={route} vehicles={vehicles} onSaved={load} onBusDeparted={() => busDeparted(route.id)} />}
      </div>
    </div>
  );
}

function RouteEditor({ route, vehicles, onSaved, onBusDeparted }: { route: Route; vehicles: Vehicle[]; onSaved: () => void; onBusDeparted: () => void }) {
  const [stops, setStops] = useState<StopInput[]>(route.stops.map((s) => ({ name: s.name, sequence: s.sequence, pickupTime: s.pickupTime, latitude: s.latitude, longitude: s.longitude })));
  const [vehicleId, setVehicleId] = useState<string | null>(route.vehicleId);
  const [busy, setBusy] = useState(false);

  const addStop = () => setStops([...stops, { name: '', sequence: stops.length + 1 }]);
  const update = (i: number, patch: Partial<StopInput>) => setStops(stops.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const remove = (i: number) => setStops(stops.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, sequence: idx + 1 })));

  const save = async () => {
    setBusy(true);
    try { await transportApi.setStops(route.id, stops.filter((s) => s.name.trim()), vehicleId); onSaved(); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 className="h-display" style={{ fontSize: 20 }}>{route.name}</h2>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={onBusDeparted}>🚌 Bus departed — notify parents</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="label">Assigned vehicle</label>
        <select className="input" value={vehicleId ?? ''} onChange={(e) => setVehicleId(e.target.value || null)} style={{ maxWidth: 260 }}>
          <option value="">— none —</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Stops (in order)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {stops.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ width: 22, color: 'var(--ink-3)' }}>{i + 1}</span>
            <input className="input" placeholder="Stop name" value={s.name} onChange={(e) => update(i, { name: e.target.value })} style={{ flex: 1 }} />
            <input className="input" placeholder="7:45 AM" value={s.pickupTime ?? ''} onChange={(e) => update(i, { pickupTime: e.target.value })} style={{ width: 100 }} />
            <button className="btn" style={{ padding: '4px 10px' }} onClick={() => remove(i)}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={addStop}>+ Add stop</button>
        <button className="btn btn-accent" disabled={busy} onClick={save} style={{ marginLeft: 'auto' }}>{busy ? 'Saving…' : 'Save stops'}</button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400, borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent' }}>{children}</button>;
}
